/**
 * The things a tech does on a job, written once for both states of the world.
 *
 * Every one of these tries the server first and falls back to the queue when
 * it cannot be reached. That order matters: online, the server is authoritative
 * — it assigns the document number, it decides whether a variation is approved,
 * it enforces the gate — and taking its answer is better than guessing. Offline,
 * the queue takes it and the device applies the same rules locally so the tech
 * sees the same thing they will see when it syncs.
 *
 * The rule that makes this safe is that the device never decides anything the
 * server owns. A variation raised here is *predicted*, not approved: the
 * prediction runs the identical pure function the server will (D18), so the
 * tech can tell the customer what is going to happen, and the server still
 * makes it happen.
 *
 * What a caller gets back is `{ result, queued }`. `queued` is not a detail to
 * swallow — "saved" and "saved on this phone, waiting to send" are different
 * things to tell someone standing in front of a customer.
 */

import { ApiError, api } from "./api";
import { standing, submit } from "./variations";
import * as outbox from "./outbox";
import * as store from "./store";
import { get, type Row } from "./store";
import * as repo from "./repo";
import type { CommissionResult, FormSubmissionOut, JobDetailOut, ReturnReason } from "./types";

export type Outcome<T> = {
  result: T;
  /** True when this is held on the device rather than accepted by the server. */
  queued: boolean;
  /** Written for a person, when there is something worth saying. */
  message?: string;
};

/** Only a transport failure falls back to the queue. */
function isUnreachable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

/**
 * A 409 is the server refusing on business grounds — the variation gate, a
 * pending approval. That is an answer, not an outage, and queueing it would
 * mean the device quietly retrying something the server has already said no
 * to. It is rethrown so the screen can show the reason.
 */
function rethrowIfAnswered(error: unknown): never | void {
  if (!isUnreachable(error)) throw error;
}

/* ------------------------------------------------------------------ jobs */

export async function arrive(jobId: string, userId?: string): Promise<Outcome<JobDetailOut | null>> {
  const at = new Date().toISOString();
  try {
    return { result: await absorb(await api.jobs.arrive(jobId)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    // Arriving is the return visit happening, so it leaves the "to book" list.
    await outbox.update("job", jobId, { arrived_at: at, status: "on_site", needs_return: false });
    // The on-site clock starts here, as it does on the server (D86) — the same
    // row, minted on the phone, so it lands once whichever way it arrives.
    if (userId && !(await runningClock(jobId, userId))) await startClock(jobId, userId, "on_site", at);
    return {
      result: await repo.job(jobId),
      queued: true,
      message: "Arrival recorded on this phone. It'll send when you're back in range.",
    };
  }
}

/* ------------------------------------------------------------ the on-site clock */

type Clocked = Row & { id: string; kind: string; ended_at?: string | null; started_at?: string; user_id?: string };

/** The clock running for this person on this job — on site, or on a break. */
export async function runningClock(jobId: string, userId: string): Promise<Clocked | null> {
  const rows = (await store.where<Clocked>("time_entry", "job_id", jobId)).filter(
    (e) =>
      !e.ended_at &&
      !e.deleted_at &&
      !Number(e.minutes ?? 0) &&
      str(e.user_id) === userId &&
      (e.kind === "on_site" || e.kind === "break"),
  );
  return rows[0] ?? null;
}

const str = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

async function startClock(jobId: string, userId: string, kind: "on_site" | "break", at: string): Promise<void> {
  const user = await get<Row>("user_account", userId);
  await outbox.create("time_entry", {
    job_id: jobId,
    user_id: userId,
    kind,
    started_at: at,
    minutes: 0,
    billable: kind === "on_site",
    cost_cents: Number(user?.hourly_cost_cents ?? 0),
    charge_cents: kind === "on_site" ? Number(user?.hourly_charge_cents ?? 0) : 0,
  });
}

async function stopClock(entry: Clocked, at: string): Promise<void> {
  const minutes = Math.max(0, Math.round((Date.parse(at) - Date.parse(str(entry.started_at))) / 60_000));
  await outbox.update("time_entry", entry.id, { ended_at: at, minutes });
}

/**
 * Pause the on-site clock — lunch (F7, D86). The running entry stops and a
 * non-billable break starts, online through the server and offline on the
 * phone with the same rows, so the service-call blocks count work, not lunch.
 */
export async function takeBreak(jobId: string, userId: string): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await absorb(await api.jobs.takeBreak(jobId)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    const running = await runningClock(jobId, userId);
    if (!running || running.kind !== "on_site") {
      throw new ApiError(409, running ? "Already on a break — resume first." : "The clock isn't running — arrive first.");
    }
    const at = new Date().toISOString();
    await stopClock(running, at);
    await startClock(jobId, userId, "break", at);
    return { result: await repo.job(jobId), queued: true, message: "On a break. It'll send when you're back in range." };
  }
}

/** Back to work: the break stops and the on-site clock starts again. */
export async function resumeWork(jobId: string, userId: string): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await absorb(await api.jobs.resume(jobId)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    const running = await runningClock(jobId, userId);
    if (!running || running.kind !== "break") throw new ApiError(409, "Not on a break on this job.");
    const at = new Date().toISOString();
    await stopClock(running, at);
    await startClock(jobId, userId, "on_site", at);
    return { result: await repo.job(jobId), queued: true, message: "Back on the clock. It'll send when you're back in range." };
  }
}

/**
 * How the job bills, and how far it is (D86). Queued like the write-up when
 * there is no signal; the server refuses it on a job already billed.
 */
export async function setBilling(
  jobId: string,
  fields: { billing_mode?: string; travel_km_one_way?: number | null },
): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await absorb(await api.jobs.writeUp(jobId, fields)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.update("job", jobId, fields);
    return { result: await repo.job(jobId), queued: true, message: "Saved on this phone." };
  }
}

/**
 * Change what a line charges (F2). Online only, like a quantity change: the
 * server decides whether the job can still change (a billed job cannot,
 * D82), and the device takes the answer through `absorb` like everything
 * else so the margin beside it moves too.
 */
export async function changePrice(jobId: string, lineId: string, unitSellCents: number): Promise<JobDetailOut | null> {
  return absorb(await api.jobs.correctLine(jobId, lineId, { unit_sell_cents: unitSellCents }));
}

/**
 * The work is done (F1). Online it is the server's `/complete`, whose answer
 * says whether the job now needs invoicing and roughly what for (F5). With no
 * signal the status is queued like arrival is, and the office's "to invoice"
 * list picks the job up when it lands; the invoice check is the server's, so
 * it is not guessed at here.
 */
export async function completeJob(jobId: string): Promise<Outcome<JobDetailOut | null>> {
  const at = new Date().toISOString();
  try {
    return { result: await absorb(await api.jobs.complete(jobId)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.update("job", jobId, { completed_at: at, status: "completed", needs_return: false });
    // Finishing stops every clock still running on the job (F6), as the
    // server does.
    for (const entry of await store.where<Clocked>("time_entry", "job_id", jobId)) {
      const clock = (entry.kind === "on_site" || entry.kind === "break") && !Number(entry.minutes ?? 0);
      if (clock && !entry.ended_at && !entry.deleted_at) {
        await stopClock(entry, at);
      }
    }
    return {
      result: await repo.job(jobId),
      queued: true,
      message: "Finished on this phone. It'll send when you're back in range, and the office will see it to invoice.",
    };
  }
}

/**
 * A discount or buffer on the job (F9): ±basis points or a fixed ±amount, never
 * both — setting one clears the other, as the server does. Before GST; it is
 * its own line on the invoice. The office's call (the server refuses a tech),
 * and online only, like a price change: the server decides whether the job can
 * still change, and a phone does not sync it.
 */
export async function setAdjustment(
  jobId: string,
  adjustment: { bp?: number | null; cents?: number | null; label?: string | null },
): Promise<Outcome<JobDetailOut | null>> {
  const bp = adjustment.bp ?? null;
  const cents = bp === null ? (adjustment.cents ?? null) : null;
  const fields = {
    adjustment_bp: bp,
    adjustment_cents: cents,
    adjustment_label: (adjustment.label ?? "").trim() || null,
  };
  try {
    return { result: await absorb(await api.jobs.writeUp(jobId, fields)), queued: false };
  } catch (error) {
    if (isUnreachable(error)) {
      throw new ApiError(0, "A discount or buffer needs a connection — try again when you're back in range.");
    }
    throw error;
  }
}

/**
 * Off site for today, coming back (D87) — not "Job finished". The leaving
 * tech's clock stops, the job is flagged with why, what's needed and how long,
 * and it sits on the office's "to book" list until a later visit is booked or
 * someone arrives. Parts move it to awaiting parts. With no signal the same
 * rows are queued, so it lands once whichever way it arrives.
 */
export async function leaveSite(
  jobId: string,
  userId: string,
  plan: { reason: ReturnReason; note?: string | null; hours?: number | null },
): Promise<Outcome<JobDetailOut | null>> {
  const note = (plan.note ?? "").trim() || null;
  if ((plan.reason === "parts" || plan.reason === "other") && !note) {
    throw new ApiError(422, "Say which parts, or what's left — the office books the next visit from it.");
  }
  const body = { reason: plan.reason, note, hours: plan.hours ?? null };
  try {
    return { result: await absorb(await api.jobs.leave(jobId, body)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    const job = await get<Row>("job", jobId);
    if (job && (job.status === "completed" || job.status === "invoiced")) {
      throw new ApiError(409, "This job is already finished.");
    }
    const at = new Date().toISOString();
    const running = await runningClock(jobId, userId);
    if (running) await stopClock(running, at);
    await outbox.update("job", jobId, {
      needs_return: true,
      return_reason: plan.reason,
      return_note: note,
      return_hours: plan.hours ?? null,
      left_site_at: at,
      status: plan.reason === "parts" ? "awaiting_parts" : "in_progress",
    });
    return {
      result: await repo.job(jobId),
      queued: true,
      message: "Saved on this phone. The office will see it to book when you're back in range.",
    };
  }
}

/**
 * What was done on site, and the customer's order number — the words the
 * invoice and the docket print. Written on the phone as often as in the
 * office, so it queues like everything else a tech does.
 */
export async function writeUp(
  jobId: string,
  fields: { work_done?: string; purchase_order_number?: string },
): Promise<Outcome<JobDetailOut | null>> {
  try {
    const result = await api.jobs.writeUp(jobId, fields);
    await store.patch("job", jobId, fields);
    return { result, queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.update("job", jobId, fields);
    return {
      result: await repo.job(jobId),
      queued: true,
      message: "Saved on this phone. It'll send when you're back in range.",
    };
  }
}

/**
 * Write what the server just said back into the device's own store.
 *
 * Every screen here reads from IndexedDB, not from the response — that is
 * what makes a job card open in a comms room. The consequence is that an
 * action which succeeds online and does not write the result back leaves the
 * screen showing what the device believed a moment ago: a tech removes a
 * part, the row stays, the margin does not move, and they tap it again.
 *
 * So the online path of every job action ends here. Lines the server no
 * longer has are removed locally too, which is the half that a patch-only
 * update cannot express (D70).
 */
async function absorb(job: JobDetailOut | null): Promise<JobDetailOut | null> {
  if (!job) return job;
  const { lines, time_entries: times, costing: _costing, stock: _stock, completion: _completion, ...rest } = job;
  await store.put("job", rest as store.Row);
  for (const entry of times ?? []) {
    await store.put("time_entry", { ...entry, job_id: job.id } as store.Row);
  }
  // A response without lines is a response that says nothing about them, not
  // one that says there are none — never take it as an instruction to empty
  // the device's copy.
  if (!Array.isArray(lines)) return job;
  for (const line of lines) {
    await store.put("job_line", { ...line, job_id: job.id } as store.Row);
  }
  const held = await store.where<store.Row>("job_line", "job_id", job.id);
  const live = new Set(lines.map((line) => line.id));
  for (const line of held) {
    if (!live.has(line.id) && !line.deleted_at) await store.remove("job_line", line.id);
  }
  return job;
}

export async function addLine(jobId: string, line: Record<string, unknown>): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await absorb(await api.jobs.addLine(jobId, line)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.create("job_line", { ...line, job_id: jobId });
    return { result: await repo.job(jobId), queued: true, message: "Part added on this phone." };
  }
}

/**
 * Change how many of a line there are.
 *
 * Online only. A cut puts the difference back on the van it came off, and a
 * phone cannot be trusted to adjust a van's count on its own (D70) — two phones
 * correcting the same line would each apply the return. With no signal the
 * honest options are taking the line off and adding it again, both of which
 * queue, and the error says so rather than pretending to have saved.
 */
export async function changeQty(jobId: string, lineId: string, qty: number): Promise<JobDetailOut | null> {
  return absorb(await api.jobs.correctLine(jobId, lineId, { qty }));
}

/**
 * Move a line to another job of the same customer.
 *
 * Online only. A move gives the part back to the van and takes it again for
 * the new job, and a phone cannot be trusted to do that on its own (D70) — two
 * phones moving the same line would each return and re-take it. The answer is
 * the TARGET job; the source is re-read afterwards so the moved line leaves
 * the device's copy of it too, rather than showing on both jobs.
 */
export async function moveLine(jobId: string, lineId: string, toJobId: string): Promise<JobDetailOut | null> {
  const target = await absorb(await api.jobs.moveLine(jobId, lineId, toJobId));
  await absorb(await api.jobs.get(jobId));
  return target;
}

/**
 * Take a line back off, and give the part back to the van.
 *
 * Queues like everything else, and the stock half happens on the server when
 * it lands — a phone cannot be trusted to adjust a van's count on its own,
 * because two phones correcting the same line would each apply the return
 * (D70). What the device does locally is drop the line, which is what the
 * tech needs to see.
 */
export async function removeLine(jobId: string, lineId: string): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await absorb(await api.jobs.removeLine(jobId, lineId)), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.destroy("job_line", lineId);
    return { result: await repo.job(jobId), queued: true, message: "Taken off on this phone." };
  }
}

/**
 * Call the job off. Office only, and online only: it is a decision made at a
 * desk with the customer on the phone, not something a van queues.
 */
export async function cancelJob(jobId: string, reason: string): Promise<JobDetailOut | null> {
  return absorb(await api.jobs.cancel(jobId, reason));
}

/**
 * Book time, and refuse it here if the server would.
 *
 * The gate is checked locally before queueing, because the whole point of it
 * is that the conversation happens while the tech is on site. Queueing time
 * the server is going to reject, and telling them about it that evening, is
 * the failure the gate exists to prevent — only later and with less chance of
 * fixing it.
 */
export async function bookTime(
  jobId: string,
  entry: { minutes: number; user_id: string; kind?: string; billable?: boolean },
): Promise<Outcome<JobDetailOut | null>> {
  try {
    const result = await absorb(await api.jobs.bookTime(jobId, entry));
    return { result, queued: false, message: result?.notice ?? undefined };
  } catch (error) {
    rethrowIfAnswered(error);

    const blocked = await wouldBeBlocked(jobId, entry.minutes / 60);
    if (blocked) {
      throw new ApiError(409, blocked, { block: "local", detail: blocked });
    }

    const user = await get<Row>("user_account", entry.user_id);
    await outbox.create("time_entry", {
      job_id: jobId,
      user_id: entry.user_id,
      kind: entry.kind ?? "on_site",
      // Booked time has happened: it ends now, so it is never taken for a
      // clock still running (D86). The server does the same.
      started_at: new Date(Date.now() - entry.minutes * 60_000).toISOString(),
      ended_at: new Date().toISOString(),
      minutes: entry.minutes,
      billable: entry.billable ?? true,
      // Snapshotted here, as the server does: a rate change tomorrow must not
      // rewrite what today's hours cost.
      cost_cents: Number(user?.hourly_cost_cents ?? 0),
      charge_cents: Number(user?.hourly_charge_cents ?? 0),
    });

    const result = await repo.job(jobId);
    const finished = result?.status === "completed" || result?.status === "invoiced";
    return {
      result,
      queued: true,
      // Allowed, but said (D87) — the same words the server uses.
      message: finished
        ? "Time booked on this phone. This job is finished — the office will see the change before invoicing."
        : "Time booked on this phone.",
    };
  }
}

/** The same check the server runs, against what this device is holding. */
async function wouldBeBlocked(jobId: string, proposedHours: number): Promise<string | null> {
  const job = await repo.job(jobId);
  if (!job) return null;

  const rows = await repo.variations(jobId);
  const where = standing(
    rows.map((v) => ({
      status: String(v.status ?? "draft"),
      amount_cents: Number(v.amount_cents ?? 0),
      hours: Number(v.hours ?? 0),
    })),
    {
      quoted_hours: job.quoted_hours,
      hours_actual: job.costing?.hours_actual ?? 0,
      overrun_accepted_hours: job.overrun_accepted_hours,
      proposed_hours: proposedHours,
    },
  );

  return where.block === "none" ? null : (where.warnings[0] ?? "This job needs approval before more time.");
}

/* ------------------------------------------------------------ variations */

export type RaisedVariation = {
  id: string;
  status: string;
  message: string;
  needs_office: boolean;
  /** True when this is the device's prediction rather than the server's ruling. */
  predicted: boolean;
};

/**
 * Raise a variation, and tell the tech what it means.
 *
 * Offline the outcome is *predicted* by running the same pure function the
 * server will (D18). The prediction needs the org's field approval limit,
 * which the working set carries — and if it does not, the honest answer is
 * "waiting on the office" rather than a guess that might have the tech telling
 * a customer their $4,000 variation is approved.
 */
export async function raiseVariation(
  jobId: string,
  payload: {
    description: string;
    amount_cents: number;
    hours?: number | null;
    customer_signature_url?: string | null;
    accepted_by_name?: string | null;
    submit?: boolean;
  },
): Promise<Outcome<RaisedVariation>> {
  try {
    const response = await api.variations.raise(jobId, payload);
    return {
      result: {
        id: response.variation.id,
        status: response.variation.status,
        message: response.message,
        needs_office: response.needs_office,
        predicted: false,
      },
      queued: false,
    };
  } catch (error) {
    rethrowIfAnswered(error);

    const orgs = await repo.org();
    const limit = orgs === null ? 0 : Number(orgs.field_approval_limit_cents ?? 0);
    const outcome =
      payload.submit === false
        ? { status: "draft", needs_office: false, reason: "Saved as a draft." }
        : submit({
            amount_cents: payload.amount_cents,
            field_approval_limit_cents: limit,
            has_signature: Boolean(payload.customer_signature_url),
          });

    const row = await outbox.create(
      "variation",
      {
        job_id: jobId,
        description: payload.description,
        amount_cents: payload.amount_cents,
        hours: payload.hours ?? null,
        customer_signature_url: payload.customer_signature_url ?? null,
        accepted_by_name: payload.accepted_by_name ?? null,
        // Local only: `status` is not in the sync whitelist, so this is what the
        // screen shows until the server rules on it. It is never sent.
        status: outcome.status,
      },
      { localOnly: ["status"] },
    );

    return {
      result: {
        id: row.id,
        status: outcome.status,
        message: outcome.reason,
        needs_office: outcome.needs_office,
        predicted: true,
      },
      queued: true,
    };
  }
}

/* ---------------------------------------------------------------- quotes */

export type SavedQuote = {
  id: string;
  /** The number the customer was shown: real if online, provisional if not. */
  number: string;
  total_cents: number;
  predicted: boolean;
};

export type QuoteDraft = {
  customer_id: string;
  site_id: string;
  title: string;
  target_margin_bp: number;
  sections: {
    kit_id: string;
    title: string;
    driver_qty: number;
    include_optional_item_ids?: string[];
  }[];
};

/**
 * Save a quote built at a site walk.
 *
 * Online this is one call and the server does the expansion. Offline it is a
 * three-level create — quote, sections, lines — assembled here and queued. The
 * projector ranks creates so a section cannot land before its quote and a line
 * cannot land before its section (D14), which is what makes sending all three
 * in one batch safe.
 *
 * Two things the device does *not* decide, and both matter:
 *
 *  * **The number.** A provisional `Q-D4-0003` is minted so the customer can
 *    be handed something, and the server swaps in the real one at sync while
 *    keeping the provisional in `client_ref` (D10).
 *  * **The totals.** `cost_cents` and friends are not writable from a device.
 *    The device prices the quote so it can show a number on site; the server
 *    prices it again when the batch lands. Those are the same number because
 *    both pricing engines are held to the same fixtures (D23) — which is the
 *    entire reason that work was worth doing.
 */
export async function saveQuote(draft: QuoteDraft): Promise<Outcome<SavedQuote>> {
  try {
    const quote = await api.quotes.create({
      customer_id: draft.customer_id,
      site_id: draft.site_id,
      title: draft.title,
      target_margin_bp: draft.target_margin_bp,
      built_on_site: true,
      sections: draft.sections.map((s) => ({
        kit_id: s.kit_id,
        title: s.title,
        driver_qty: s.driver_qty,
        include_optional_item_ids: s.include_optional_item_ids ?? [],
      })),
    });
    return {
      result: { id: quote.id, number: quote.number, total_cents: quote.total_cents, predicted: false },
      queued: false,
    };
  } catch (error) {
    rethrowIfAnswered(error);
    return queueQuote(draft);
  }
}

async function queueQuote(draft: QuoteDraft): Promise<Outcome<SavedQuote>> {
  const quote = await outbox.create(
    "quote",
    {
      customer_id: draft.customer_id,
      site_id: draft.site_id,
      title: draft.title,
      target_margin_bp: draft.target_margin_bp,
      status: "draft",
      built_on_site: true,
    },
    { numberPrefix: "Q", localOnly: ["status"] },
  );

  let total = 0;

  for (const [sort, section] of draft.sections.entries()) {
    // Expanded here rather than trusting whatever the screen had in hand: the
    // preview is a view, and a quote is a record. Re-running the expansion
    // means what is stored is what the rules say, not what a component held.
    const preview = await repo.kitPreview(section.kit_id, section.driver_qty, {
      targetMarginBp: draft.target_margin_bp,
      includeOptional: new Set(section.include_optional_item_ids ?? []),
    });
    if (!preview) continue;
    total += preview.totals.total_cents;

    const row = await outbox.create("quote_section", {
      quote_id: quote.id,
      kit_id: section.kit_id,
      title: section.title,
      driver_qty: section.driver_qty,
      is_optional: false,
      sort,
    });

    for (const [lineSort, line] of preview.lines.entries()) {
      await outbox.create("quote_line", {
        section_id: row.id,
        // Both of these were missing until ASSET-3's browser pass: the line
        // priced correctly, so nothing looked wrong, but acceptance keys on
        // them. Without `catalogue_item_id` a quote built in the car park
        // reserved no stock, drafted no PO and gave the job lines nothing to
        // take off the van; without `creates_asset` it pre-registered not
        // one camera. Every quote built offline, silently (D48).
        catalogue_item_id: line.item_id,
        creates_asset: line.creates_asset,
        description: line.description,
        kind: line.kind,
        qty: line.qty,
        uom: line.uom,
        unit_cost_cents: line.unit_cost_cents,
        unit_sell_cents: line.unit_sell_cents,
        sort: lineSort,
      });
    }
  }

  // Stored locally so the quote shows its own total before it has synced.
  // Not queued — see the note above about who owns the money columns.
  await store.patch("quote", quote.id, { total_cents: total });

  return {
    result: {
      id: quote.id,
      number: String(quote.client_ref ?? "—"),
      total_cents: total,
      predicted: true,
    },
    queued: true,
    message: `Saved on this phone as ${String(quote.client_ref)}. It'll send when you're back in range.`,
  };
}

/* ---------------------------------------------------------------- assets */

export type ScanDetails = {
  location_desc?: string;
  firmware?: string;
  ip_address?: string;
};

/** Trim what a scanner adds and a human types — the server's `normalise`. */
export function normaliseSerial(raw: string): string {
  return raw.split(/\s+/).filter(Boolean).join(" ");
}

/**
 * Scan a serial onto an asset shell.
 *
 * Online, the server does everything: duplicate check across the whole
 * register, install date, warranty recounted from the real term. Offline —
 * which is most of the time, because this happens in a plant room — the
 * device queues the *fact* as an append-only `installed` event and the server
 * derives the same consequences from it when the phone finds signal (D47).
 *
 * Two things the phone can do on its own, and does:
 *
 *  * refuse a serial it can already see on a different asset. That is only
 *    the assets this device holds, not the whole register, but it catches the
 *    common mistake — the box of the camera next to it — while the tech is
 *    still standing there. The server catches the rest.
 *  * show the serial on the asset straight away, so the worklist shrinks as
 *    they go. That is a local patch, not a queued write: the server does not
 *    let a device set a serial directly, and would refuse it.
 *
 * What it does not do is guess the warranty. It does not know the term, and a
 * made-up date on a screen is a date somebody reads out to a customer.
 */
export async function commission(
  assetId: string,
  rawSerial: string,
  details: ScanDetails & { job_id?: string | null } = {},
): Promise<Outcome<CommissionResult | null>> {
  const serial = normaliseSerial(rawSerial);
  if (!serial) throw new ApiError(422, "Scan or type the serial first.");

  // A scan the server refused for this asset is answered by this one, whatever
  // happens next: the tech has seen the reason and is fixing it. Left in the
  // queue, it would keep this asset showing as refused after a scan that
  // worked.
  const answered = async () => {
    const stale = (await outbox.rejected()).filter(
      (c) =>
        c.entity === "asset_event" &&
        (c.value as Record<string, unknown> | null)?.asset_id === assetId &&
        (c.value as Record<string, unknown> | null)?.kind === "installed",
    );
    if (stale.length) await outbox.acknowledgeRejected(stale.map((c) => c.seq!).filter((n) => n !== undefined));
  };

  try {
    const result = await api.assets.commission(assetId, { serial, ...details });
    await answered();
    // The server's version of the row, onto the phone now rather than at the
    // next pull — otherwise the worklist does not shrink after a scan that
    // worked, and the tech scans it again to be sure.
    await store.patch("asset", assetId, { ...result.asset, pending_install: false });
    return { result, queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
  }

  const here = await get<Row>("asset", assetId);
  const known = await store.all<Row>("asset");
  // A serial the server already refused is not one this phone should hold
  // against the next asset — that is the box they are now trying correctly.
  const states = await repo.installStates();
  const clash = known.find(
    (a) =>
      a.id !== assetId &&
      !a.deleted_at &&
      states.get(a.id)?.state !== "refused" &&
      typeof a.serial === "string" &&
      a.serial.toLowerCase() === serial.toLowerCase(),
  );
  if (clash) {
    throw new ApiError(409, `Serial ${serial} is already on ${String(clash.ref)}. Check you have the right box.`);
  }
  if (
    states.get(assetId)?.state !== "refused" &&
    typeof here?.serial === "string" &&
    here.serial.toLowerCase() === serial.toLowerCase()
  ) {
    return { result: null, queued: false, message: `${String(here.ref)} is already this serial — nothing changed.` };
  }

  const payload = Object.fromEntries(
    Object.entries({ serial, ...details }).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  await answered();
  await outbox.create("asset_event", {
    asset_id: assetId,
    job_id: details.job_id ?? here?.installed_by_job_id ?? null,
    kind: "installed",
    occurred_at: new Date().toISOString(),
    summary: `Scanned ${serial}`,
    payload,
  });
  await store.patch("asset", assetId, {
    serial,
    ...(details.location_desc ? { location_desc: details.location_desc } : {}),
    pending_install: true,
  });

  return {
    result: null,
    queued: true,
    message: `${String(here?.ref ?? "Asset")} scanned on this phone. The warranty date is worked out when it sends.`,
  };
}

/* ------------------------------------------------------ tests (COMPLY-1) */

/**
 * Submit a test sheet, online or not.
 *
 * Online the server judges it, rolls the asset's next-due date and issues the
 * certificate, and its answer comes back with the certificate number. Offline
 * the submission is queued and the phone shows what it can honestly show: the
 * result, which is pure arithmetic over the answers, and *not* a certificate
 * number, because the number comes from a sequence only the server holds. A
 * phone that invented one would hand a customer a certificate that does not
 * exist (D15).
 */
export async function submitTestSheet(payload: {
  template_id: string;
  asset_id?: string | null;
  job_id?: string | null;
  answers: Record<string, unknown>;
  notes?: string | null;
  signed_by?: string | null;
}): Promise<Outcome<FormSubmissionOut | null>> {
  const signedBy = payload.signed_by?.trim() || null;
  const body = {
    ...payload,
    notes: payload.notes?.trim() || null,
    signed_by: signedBy,
    tested_at: new Date().toISOString(),
  };
  try {
    const result = await api.forms.submit(body);
    if (result.asset_id) {
      await store.patch("asset", result.asset_id, {
        last_test_on: result.submitted_at?.slice(0, 10) ?? null,
        next_test_due_on: result.next_test_due_on,
        ...(result.result === "fail" ? { status: "fault" } : {}),
      });
    }
    return { result, queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
  }

  await outbox.create("form_submission", {
    template_id: payload.template_id,
    asset_id: payload.asset_id ?? null,
    job_id: payload.job_id ?? null,
    answers: {
      ...payload.answers,
      ...(body.notes ? { _notes: body.notes } : {}),
    },
    submitted_at: body.tested_at,
  });
  return {
    result: null,
    queued: true,
    message: "Test sheet saved on this phone. The certificate is issued when it sends.",
  };
}
